import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Procedures from './pages/Procedures';
import ProcedureDetail from './pages/ProcedureDetail';
import ProcedureForm from './pages/ProcedureForm';
import Structures from './pages/Structures';
import Utilisateurs from './pages/Utilisateurs';
import Statistiques from './pages/Statistiques';
import Analytique from './pages/Analytique';
import AdminPhases from './pages/AdminPhases';
import Communication from './pages/Communication';

const PrivateRoute: React.FC<{
  children: React.ReactNode;
  adminOnly?: boolean;
  adminOrResponsable?: boolean;
}> = ({ children, adminOnly = false, adminOrResponsable = false }) => {
  const { isAuthenticated, isLoading, isSuperAdmin, isResponsable } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isSuperAdmin()) {
    return <Navigate to="/" replace />;
  }

  if (adminOrResponsable && !isSuperAdmin() && !isResponsable()) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/procedures"
        element={
          <PrivateRoute>
            <Procedures />
          </PrivateRoute>
        }
      />
      <Route
        path="/procedures/new"
        element={
          <PrivateRoute>
            <ProcedureForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/procedures/:id"
        element={
          <PrivateRoute>
            <ProcedureDetail />
          </PrivateRoute>
        }
      />
      <Route
        path="/procedures/:id/edit"
        element={
          <PrivateRoute>
            <ProcedureForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/structures"
        element={
          <PrivateRoute adminOnly>
            <Structures />
          </PrivateRoute>
        }
      />
      <Route
        path="/utilisateurs"
        element={
          <PrivateRoute adminOrResponsable>
            <Utilisateurs />
          </PrivateRoute>
        }
      />
      <Route
        path="/statistiques"
        element={
          <PrivateRoute>
            <Statistiques />
          </PrivateRoute>
        }
      />
      <Route
        path="/analytique"
        element={
          <PrivateRoute adminOnly>
            <Analytique />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/phases"
        element={
          <PrivateRoute adminOnly>
            <AdminPhases />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/communication"
        element={
          <PrivateRoute adminOnly>
            <Communication />
          </PrivateRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
};

export default App;
